import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { EmployeeService } from 'src/app/services/employee.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';

interface RestDayOption {
    value: string;
    label: string;
}

@Component({
    selector: 'app-salary-tab',
    templateUrl: './salary-tab.component.html',
    styleUrls: ['./salary-tab.component.css', '../employee-tab-shared.css']
})
export class SalaryTabComponent implements OnInit {
    @Input() employee: any;
    @Output() saved = new EventEmitter<any>();

    form!: FormGroup;

    /** Los valores deben coincidir con lo que ya guarda la BD (español) —
     * antes usaban claves en ingles y los checkboxes nunca reflejaban el
     * estado real, borrando rest_days en cada guardado. */
    restDayOptions: RestDayOption[] = [
        { value: 'Domingo', label: 'Domingo' },
        { value: 'Lunes', label: 'Lunes' },
        { value: 'Martes', label: 'Martes' },
        { value: 'Miércoles', label: 'Miércoles' },
        { value: 'Jueves', label: 'Jueves' },
        { value: 'Viernes', label: 'Viernes' },
        { value: 'Sábado', label: 'Sábado' }
    ];

    constructor(
        private fb: FormBuilder,
        private employeeService: EmployeeService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Listado de colaboradores', action);
    }

    ngOnInit(): void {
        const restDays: string[] = Array.isArray(this.employee?.rest_days) ? this.employee.rest_days : [];

        const restDaysGroup: any = {};
        this.restDayOptions.forEach((opt) => {
            restDaysGroup[opt.value] = [restDays.includes(opt.value)];
        });

        this.form = this.fb.group({
            current_salary: [this.employee?.current_salary ?? ''],
            full_time: [!!this.employee?.full_time],
            daily_salary: [this.employee?.daily_salary ?? ''],
            // Automatico: Salario por dia / Horas por dia.
            hourly_salary: [{ value: this.employee?.hourly_salary ?? '', disabled: true }],
            // Automatico (jornada estandar de 8 horas), igual que en Aspel NOI.
            hours_per_day: [{ value: this.employee?.hours_per_day ?? 8, disabled: true }],
            // Automaticos (7 dias, semana completa incluyendo el dia de
            // descanso pagado) — ya no se capturan a mano, igual que en Aspel NOI.
            days_per_week: [{ value: this.employee?.days_per_week ?? 7, disabled: true }],
            days_per_period: [{ value: this.employee?.days_per_period ?? 7, disabled: true }],
            salary_effective_date: [this.toDateInputValue(this.employee?.salary_effective_date)],
            payment_method: [this.employee?.payment_method || ''],
            restDays: this.fb.group(restDaysGroup),
            bank: [this.employee?.bank || ''],
            account_number: [this.employee?.account_number || ''],
            account_holder_name: [this.employee?.account_holder_name || ''],
            account_type: [this.employee?.account_type || ''],
            fonacot_number: [this.employee?.fonacot_number || ''],
            vacation_table: [{ value: this.employee?.vacation_table || '', disabled: true }],
            imss_contribution_base_type: [this.employee?.imss_contribution_base_type || ''],
            sdi_table: [{ value: this.employee?.sdi_table || '', disabled: true }],
            calculated_sdi: [{ value: this.employee?.calculated_sdi ?? '', disabled: true }],
            entered_sdi: [this.employee?.entered_sdi ?? '']
        });

        // "SDI calculado" debe reflejar el salario por dia al momento, sin
        // esperar a guardar — el backend vuelve a calcularlo igual al
        // guardar (ver SdiCalculator.php), esto es solo la vista previa.
        this.form.get('daily_salary')?.valueChanges.subscribe(() => this.previewCalculatedSdi());
        // La antigüedad se evalúa a la fecha de aplicación del salario, así
        // que cambiarla también debe refrescar la vista previa del SDI.
        this.form.get('salary_effective_date')?.valueChanges.subscribe(() => this.previewCalculatedSdi());

        // "Salario por hora" = Salario por dia / Horas por dia.
        this.form.get('daily_salary')?.valueChanges.subscribe(() => this.recalculateHourlySalary());
        this.form.get('hours_per_day')?.valueChanges.subscribe(() => this.recalculateHourlySalary());

        // Vista previa inicial: si ya hay fecha de aplicación/salario
        // capturados, mostrar de una vez el valor que realmente se
        // guardaría, en vez del último valor persistido (que puede haber
        // quedado desactualizado si la lógica de cálculo cambió).
        this.previewCalculatedSdi();
    }

    private recalculateHourlySalary(): void {
        const dailySalary = parseFloat(this.form.get('daily_salary')?.value);
        const hoursPerDay = parseFloat(this.form.get('hours_per_day')?.value);
        if (!dailySalary || dailySalary <= 0 || !hoursPerDay || hoursPerDay <= 0) {
            return;
        }

        const hourlySalary = Math.round((dailySalary / hoursPerDay) * 100) / 100;
        this.form.get('hourly_salary')?.setValue(hourlySalary, { emitEvent: false });
    }

    /** Replica SdiCalculator.php (Factor de Integracion + tabla de
     * vacaciones LFT reformada por antigüedad) para mostrar "Tabla de
     * vacaciones"/"SDI calculado" en vivo mientras se edita el salario,
     * antes de guardar. La antigüedad se calcula Fecha de aplicación del
     * salario → Fecha actual (hoy) — no Fecha de ingreso → hoy — igual que
     * en el backend. Si no hay fecha de aplicación capturada, se usa la
     * fecha de ingreso como respaldo.
     *
     * "Tabla de vacaciones" solo depende de la fecha (no del salario), así
     * que se recalcula apenas se elige la fecha de aplicación, sin esperar
     * a que también haya un salario por día capturado. */
    private previewCalculatedSdi(): void {
        const baseDate = this.form.get('salary_effective_date')?.value || this.employee?.hire_date;
        if (!baseDate) {
            return;
        }

        const years = this.seniorityYears(baseDate);
        const vacationDays = this.vacationDaysForSeniority(years);
        this.form.get('vacation_table')?.setValue(String(vacationDays), { emitEvent: false });
        this.form.get('sdi_table')?.setValue(String(vacationDays), { emitEvent: false });

        const dailySalary = parseFloat(this.form.get('daily_salary')?.value);
        if (!dailySalary || dailySalary <= 0) {
            return;
        }

        const factorIntegracion = (365 + 15 + vacationDays * 0.25) / 365;
        const sdi = Math.trunc(dailySalary * factorIntegracion * 100) / 100;

        this.form.get('calculated_sdi')?.setValue(sdi, { emitEvent: false });
    }

    /** Extrae año/mes/día de un string de fecha ("YYYY-MM-DD..."), sin pasar
     * por el constructor de Date: hire_date llega del API como ISO con hora
     * (p.ej. "2021-06-01T05:00:00Z", que en la zona horaria del navegador
     * puede o no coincidir con la medianoche local) mientras que la fecha de
     * aplicación viene de un <input type="date"> como "YYYY-MM-DD" a secas
     * (interpretado como medianoche UTC) — comparar ambos con getMonth()/
     * getDate() locales puede desfasar un día entre sí según la zona horaria
     * del navegador. Comparar las partes de la fecha como texto evita por
     * completo esa ambigüedad. */
    private parseDateParts(value: any): { year: number; month: number; day: number } | null {
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
        if (!match) return null;
        return { year: +match[1], month: +match[2] - 1, day: +match[3] };
    }

    /** Antigüedad = baseDate (fecha de aplicación del salario, o fecha de
     * ingreso como respaldo) → hoy. */
    private seniorityYears(baseDate: string): number {
        const base = this.parseDateParts(baseDate);
        if (!base) return 0;

        const now = new Date();
        const today = { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() };

        let years = today.year - base.year;
        const hasHadAnniversary =
            today.month > base.month ||
            (today.month === base.month && today.day >= base.day);
        if (!hasHadAnniversary) years--;

        return Math.max(0, years);
    }

    /** Tabla de vacaciones LFT reformada 2023 — igual a SdiCalculator.php.
     * Antes de cumplir 1 año se usan los mismos 12 días del año 1 (no 0). */
    private vacationDaysForSeniority(years: number): number {
        const table: { [key: number]: number } = { 1: 12, 2: 14, 3: 16, 4: 18, 5: 20 };
        if (years <= 0) return table[1];
        if (table[years]) return table[years];
        if (years < 5) return table[1];

        const extraBlocks = Math.ceil((years - 5) / 5);
        return 20 + extraBlocks * 2;
    }

    setFullTime(value: boolean): void {
        this.form.get('full_time')?.setValue(value);
    }

    toDateInputValue(value: any): string {
        if (!value) return '';
        return String(value).substring(0, 10);
    }

    save(): void {
        const value = this.form.getRawValue();

        const restDays = this.restDayOptions
            .filter((opt) => value.restDays[opt.value])
            .map((opt) => opt.value);

        const payload: any = {
            current_salary: value.current_salary || null,
            full_time: value.full_time,
            daily_salary: value.daily_salary || null,
            hourly_salary: value.hourly_salary || null,
            hours_per_day: value.hours_per_day || null,
            days_per_week: value.days_per_week || null,
            days_per_period: value.days_per_period || null,
            salary_effective_date: value.salary_effective_date || null,
            payment_method: value.payment_method,
            rest_days: restDays,
            bank: value.bank,
            account_number: value.account_number,
            account_holder_name: value.account_holder_name,
            account_type: value.account_type,
            fonacot_number: value.fonacot_number,
            // vacation_table / sdi_table / calculated_sdi ya no se capturan
            // a mano: el backend los recalcula en cada guardado (antigüedad
            // + Factor de Integración, ver SdiCalculator).
            imss_contribution_base_type: value.imss_contribution_base_type || null,
            entered_sdi: value.entered_sdi || null
        };

        this.spinner.show();
        this.employeeService.update(this.employee.id, payload).subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                // vacation_table/sdi_table/calculated_sdi los recalcula el
                // backend — se reflejan aqui sin esperar a un reload.
                this.form.patchValue({
                    vacation_table: resp?.vacation_table ?? '',
                    sdi_table: resp?.sdi_table ?? '',
                    calculated_sdi: resp?.calculated_sdi ?? ''
                });
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'La información de salario se guardó correctamente', icon: 'success' });
                this.saved.emit(resp);
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo guardar la información', icon: 'error' });
            }
        });
    }
}
